import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Eye, EyeOff, Lock, User } from 'lucide-react';

const AdminLogin = () => {
  const { user, loading, login, register } = useAuth();
  const [isRegister, setIsRegister] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-900 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-terracota border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      if (isRegister) {
        const result = await register(formData.username, formData.email, formData.password);
        if (result.success) {
          const loginResult = await login(formData.username, formData.password);
          if (!loginResult.success) {
            setError(loginResult.error);
          }
        } else {
          setError(result.error);
        }
      } else {
        const result = await login(formData.username, formData.password);
        if (!result.success) {
          setError(result.error);
        }
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-900 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="font-serif text-3xl text-white mb-2">Margaret Núñez</h1>
          <p className="text-stone-400 text-sm tracking-wider uppercase">Admin Panel</p>
        </div>

        <div className="bg-stone-800 rounded-lg p-8">
          <h2 className="text-xl text-white font-semibold mb-6">
            {isRegister ? 'Create Account' : 'Sign In'}
          </h2>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg mb-6 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-stone-400 text-sm mb-2">Username</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-500" />
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="w-full bg-stone-700 border border-stone-600 rounded-lg px-10 py-3 text-white placeholder:text-stone-500 focus:outline-none focus:border-terracota transition-colors"
                  placeholder="Enter username"
                  required
                  data-testid="admin-username-input"
                />
              </div>
            </div>

            {isRegister && (
              <div>
                <label className="block text-stone-400 text-sm mb-2">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full bg-stone-700 border border-stone-600 rounded-lg px-4 py-3 text-white placeholder:text-stone-500 focus:outline-none focus:border-terracota transition-colors"
                  placeholder="Enter email"
                  required
                  data-testid="admin-email-input"
                />
              </div>
            )}

            <div>
              <label className="block text-stone-400 text-sm mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full bg-stone-700 border border-stone-600 rounded-lg px-10 py-3 text-white placeholder:text-stone-500 focus:outline-none focus:border-terracota transition-colors"
                  placeholder="Enter password"
                  required
                  data-testid="admin-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-300"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-terracota text-white py-3 rounded-lg font-semibold hover:bg-terracota/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-6"
              data-testid="admin-submit-btn"
            >
              {isSubmitting ? 'Please wait...' : (isRegister ? 'Create Account' : 'Sign In')}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => {
                setIsRegister(!isRegister);
                setError('');
              }}
              className="text-stone-400 text-sm hover:text-terracota transition-colors"
            >
              {isRegister ? 'Already have an account? Sign in' : 'Need an account? Register'}
            </button>
          </div>
        </div>

        <p className="text-center text-stone-500 text-xs mt-6">
          Protected area. Authorized personnel only.
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;
